'use client'

import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { formatPrice } from '@/lib/formatters'

interface Props {
  prices: [number, number][]
  volumes: [number, number][]
}

const TIMEFRAMES = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y', days: 365 },
  { label: 'All', days: Infinity },
] as const

function formatVol(v: number): string {
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`
  return `$${v.toFixed(0)}`
}

function formatDateLabel(ts: number, spanDays: number): string {
  const d = new Date(ts)
  if (spanDays <= 30) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  if (spanDays <= 400) return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
  return d.getFullYear().toString()
}

function formatCompact(price: number): string {
  if (price >= 1000) return `$${(price / 1000).toFixed(1)}K`
  if (price >= 1) return `$${price.toFixed(0)}`
  return `$${price.toFixed(4)}`
}

// Simple Moving Average
function calcSMA(data: [number, number][], period: number): { x: number; val: number; idx: number }[] {
  const result: { x: number; val: number; idx: number }[] = []
  for (let i = period - 1; i < data.length; i++) {
    let sum = 0
    for (let j = i - period + 1; j <= i; j++) sum += data[j][1]
    result.push({ idx: i, val: sum / period, x: 0 })
  }
  return result
}

export function PriceHistoryChart({ prices, volumes }: Props) {
  const [days, setDays] = useState(365)
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(800)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const filtered = useMemo(() => {
    if (days === Infinity) return { p: prices, v: volumes }
    return { p: prices.slice(-days), v: volumes.slice(-days) }
  }, [prices, volumes, days])

  // Chart geometry
  const priceH = 280
  const volH = 60
  const gap = 16
  const totalH = priceH + gap + volH
  const padT = 16
  const padR = width < 500 ? 80 : 92
  const padB = 28
  const padL = width < 500 ? 20 : 28
  const plotW = Math.max(1, width - padL - padR)
  const plotH = priceH - padT - padB

  // Price scales
  const { minP, maxP, rangeP, points, areaPath, ptCoords } = useMemo(() => {
    const fp = filtered.p
    if (fp.length === 0) return { minP: 0, maxP: 0, rangeP: 1, points: '', areaPath: '', ptCoords: [] as { x: number; y: number }[] }
    const vals = fp.map((d) => d[1])
    const min = Math.min(...vals)
    const max = Math.max(...vals)
    const range = max - min || 1
    const margin = range * 0.05
    const adjMin = min - margin
    const adjMax = max + margin
    const adjRange = adjMax - adjMin

    const pts = fp.map((d, i) => {
      const x = padL + (i / (fp.length - 1)) * plotW
      const y = padT + (1 - (d[1] - adjMin) / adjRange) * plotH
      return { x, y }
    })

    const line = pts.map((p) => `${p.x},${p.y}`).join(' ')
    const first = pts[0]
    const last = pts[pts.length - 1]
    const area = `M${first.x},${first.y} ${line} L${last.x},${padT + plotH} L${first.x},${padT + plotH} Z`

    return { minP: adjMin, maxP: adjMax, rangeP: adjRange, points: line, areaPath: area, ptCoords: pts }
  }, [filtered.p, padL, plotW, padT, plotH])

  // Period high/low markers
  const highLow = useMemo(() => {
    const fp = filtered.p
    if (fp.length === 0 || ptCoords.length === 0) return null
    let highIdx = 0
    let lowIdx = 0
    for (let i = 1; i < fp.length; i++) {
      if (fp[i][1] > fp[highIdx][1]) highIdx = i
      if (fp[i][1] < fp[lowIdx][1]) lowIdx = i
    }
    return {
      high: { idx: highIdx, price: fp[highIdx][1], x: ptCoords[highIdx].x, y: ptCoords[highIdx].y },
      low: { idx: lowIdx, price: fp[lowIdx][1], x: ptCoords[lowIdx].x, y: ptCoords[lowIdx].y },
    }
  }, [filtered.p, ptCoords])

  // Period return badge
  const periodReturn = useMemo(() => {
    const fp = filtered.p
    if (fp.length < 2) return null
    const first = fp[0][1]
    const last = fp[fp.length - 1][1]
    return first > 0 ? ((last - first) / first) * 100 : 0
  }, [filtered.p])

  // SMA 50 overlay
  const sma50Points = useMemo(() => {
    const fp = filtered.p
    if (fp.length < 50) return ''
    const smaData = calcSMA(fp, 50)
    // Map to chart coordinates
    const mapped = smaData.map((s) => {
      const x = padL + (s.idx / (fp.length - 1)) * plotW
      const y = padT + (1 - (s.val - minP) / rangeP) * plotH
      return `${x},${y}`
    })
    return mapped.join(' ')
  }, [filtered.p, padL, plotW, padT, plotH, minP, rangeP])

  // Volume scale
  const maxVol = useMemo(() => {
    if (filtered.v.length === 0) return 1
    return Math.max(...filtered.v.map((d) => d[1]))
  }, [filtered.v])

  // Y-axis labels (5 ticks)
  const yLabels = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => {
      const val = minP + (rangeP * (4 - i)) / 4
      const y = padT + (i / 4) * plotH
      return { val, y }
    })
  }, [minP, rangeP, padT, plotH])

  // X-axis labels
  const xLabels = useMemo(() => {
    const fp = filtered.p
    if (fp.length < 2) return []
    const spanMs = fp[fp.length - 1][0] - fp[0][0]
    const spanDays = spanMs / 86400000
    const count = width < 500 ? 4 : 6
    const labels: { x: number; label: string }[] = []
    const seen = new Set<string>()

    for (let i = 0; i < count; i++) {
      const idx = Math.round((i / (count - 1)) * (fp.length - 1))
      const x = padL + (idx / (fp.length - 1)) * plotW
      const label = formatDateLabel(fp[idx][0], spanDays)
      if (!seen.has(label)) {
        seen.add(label)
        labels.push({ x, label })
      }
    }
    return labels
  }, [filtered.p, width, padL, plotW])

  // Hover logic
  const getIdxFromX = useCallback(
    (clientX: number) => {
      const el = containerRef.current
      if (!el || filtered.p.length < 2) return null
      const rect = el.getBoundingClientRect()
      const x = clientX - rect.left
      const ratio = (x - padL) / plotW
      const idx = Math.round(ratio * (filtered.p.length - 1))
      return Math.max(0, Math.min(idx, filtered.p.length - 1))
    },
    [filtered.p, padL, plotW]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => setHoverIdx(getIdxFromX(e.clientX)),
    [getIdxFromX]
  )
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length > 0) setHoverIdx(getIdxFromX(e.touches[0].clientX))
    },
    [getIdxFromX]
  )
  const handleLeave = useCallback(() => setHoverIdx(null), [])

  // Hover data
  const hoverData = useMemo(() => {
    if (hoverIdx === null || !filtered.p[hoverIdx]) return null
    const [ts, price] = filtered.p[hoverIdx]
    const vol = filtered.v[hoverIdx]?.[1] ?? 0
    const prevPrice = hoverIdx > 0 ? filtered.p[hoverIdx - 1][1] : price
    const change = prevPrice > 0 ? ((price - prevPrice) / prevPrice) * 100 : 0
    const x = padL + (hoverIdx / (filtered.p.length - 1)) * plotW
    const y = padT + (1 - (price - minP) / rangeP) * plotH
    const date = new Date(ts).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
    return { x, y, price, vol, change, date }
  }, [hoverIdx, filtered.p, filtered.v, padL, plotW, padT, plotH, minP, rangeP])

  // Volume bar Y offset
  const volTop = priceH + gap

  // Current timeframe label for the badge
  const periodLabel = TIMEFRAMES.find((tf) => tf.days === days)?.label ?? ''

  return (
    <div>
      {/* Timeframe pills + period return badge */}
      <div className="flex items-center gap-4 mb-5">
        <div className="flex items-center gap-0 border border-zinc-800 w-fit">
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf.label}
              onClick={() => {
                setDays(tf.days)
                setHoverIdx(null)
              }}
              className={`px-4 py-2 text-xs font-medium transition-colors border-r border-zinc-800 last:border-r-0 ${
                days === tf.days
                  ? 'bg-zinc-800 text-white'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-900'
              }`}
            >
              {tf.label}
            </button>
          ))}
        </div>

        {/* Period return badge */}
        {periodReturn !== null && (
          <span
            className={`text-xs font-medium tabular-nums ${
              periodReturn >= 0 ? 'text-green-500' : 'text-red-500'
            }`}
          >
            {periodReturn >= 0 ? '+' : ''}
            {periodReturn.toFixed(1)}%
            <span className="text-zinc-400 font-normal ml-1">
              {periodLabel === 'All' ? 'all time' : `past ${periodLabel}`}
            </span>
          </span>
        )}
      </div>

      {/* Chart */}
      <div
        ref={containerRef}
        className="relative border border-zinc-800 select-none touch-none"
      >
        <svg
          width={width}
          height={totalH}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleLeave}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleLeave}
        >
          {/* Grid lines */}
          {yLabels.map((tick, i) => (
            <line
              key={i}
              x1={padL}
              y1={tick.y}
              x2={width - padR}
              y2={tick.y}
              stroke="#27272a"
              strokeWidth={1}
            />
          ))}

          {/* Y-axis labels */}
          {yLabels.map((tick, i) => (
            <text
              key={i}
              x={width - padR + 8}
              y={tick.y + 4}
              textAnchor="start"
              fill="#71717a"
              fontSize={10}
              fontFamily="monospace"
            >
              {formatPrice(tick.val)}
            </text>
          ))}

          {/* X-axis labels */}
          {xLabels.map((tick, i) => (
            <text
              key={i}
              x={tick.x}
              y={priceH - 2}
              textAnchor="middle"
              fill="#71717a"
              fontSize={10}
            >
              {tick.label}
            </text>
          ))}

          {/* Price area fill */}
          {areaPath && <path d={areaPath} fill="url(#histPriceGrad)" />}

          {/* Gradients */}
          <defs>
            <linearGradient id="histPriceGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.15} />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
          </defs>

          {/* Price line */}
          {points && (
            <polyline
              points={points}
              fill="none"
              stroke="#3b82f6"
              strokeWidth={1.5}
              strokeLinejoin="round"
            />
          )}

          {/* SMA 50 overlay */}
          {sma50Points && (
            <polyline
              points={sma50Points}
              fill="none"
              stroke="#a78bfa"
              strokeWidth={1}
              strokeDasharray="4,3"
              strokeLinejoin="round"
              opacity={0.6}
            />
          )}

          {/* Period high marker */}
          {highLow && (
            <>
              <polygon
                points={`${highLow.high.x},${highLow.high.y - 6} ${highLow.high.x - 4},${highLow.high.y + 1} ${highLow.high.x + 4},${highLow.high.y + 1}`}
                fill="#22c55e"
              />
              <text
                x={highLow.high.x}
                y={highLow.high.y - 12}
                textAnchor="middle"
                fill="#22c55e"
                fontSize={9}
                fontFamily="monospace"
              >
                {formatCompact(highLow.high.price)}
              </text>
            </>
          )}

          {/* Period low marker */}
          {highLow && (
            <>
              <polygon
                points={`${highLow.low.x},${highLow.low.y + 6} ${highLow.low.x - 4},${highLow.low.y - 1} ${highLow.low.x + 4},${highLow.low.y - 1}`}
                fill="#ef4444"
              />
              <text
                x={highLow.low.x}
                y={highLow.low.y + 18}
                textAnchor="middle"
                fill="#ef4444"
                fontSize={9}
                fontFamily="monospace"
              >
                {formatCompact(highLow.low.price)}
              </text>
            </>
          )}

          {/* Divider between price and volume */}
          <line
            x1={padL}
            y1={volTop - 4}
            x2={width - padR}
            y2={volTop - 4}
            stroke="#27272a"
            strokeWidth={1}
          />

          {/* Volume bars — green/red based on price direction */}
          {filtered.v.map(([, vol], i) => {
            const barH = maxVol > 0 ? (vol / maxVol) * volH : 0
            const x = padL + (i / Math.max(1, filtered.v.length - 1)) * plotW
            const barW = Math.max(1, plotW / filtered.v.length - 0.5)
            const priceUp = i > 0 && filtered.p[i]?.[1] >= filtered.p[i - 1]?.[1]
            const isHovered = hoverIdx === i
            const baseColor = priceUp || i === 0
              ? isHovered ? 'rgba(34,197,94,0.5)' : 'rgba(34,197,94,0.25)'
              : isHovered ? 'rgba(239,68,68,0.5)' : 'rgba(239,68,68,0.25)'
            return (
              <rect
                key={i}
                x={x - barW / 2}
                y={volTop + volH - barH}
                width={barW}
                height={barH}
                fill={baseColor}
              />
            )
          })}

          {/* Volume label */}
          <text x={width - padR + 8} y={volTop + 12} textAnchor="start" fill="#52525b" fontSize={9}>
            Vol
          </text>

          {/* Crosshair */}
          {hoverData && (
            <>
              <line
                x1={hoverData.x}
                y1={padT}
                x2={hoverData.x}
                y2={volTop + volH}
                stroke="#52525b"
                strokeWidth={1}
                strokeDasharray="3,3"
              />
              <circle
                cx={hoverData.x}
                cy={hoverData.y}
                r={4}
                fill="#3b82f6"
                stroke="#000"
                strokeWidth={1.5}
              />
            </>
          )}
        </svg>

        {/* SMA 50 legend — bottom-left inside chart */}
        {sma50Points && (
          <div className="absolute bottom-2 left-3 flex items-center gap-1.5">
            <div className="w-4 h-px bg-purple-400 opacity-60" style={{ borderTop: '1px dashed #a78bfa' }} />
            <span className="text-zinc-500 text-[9px]">SMA 50</span>
          </div>
        )}

        {/* Tooltip */}
        {hoverData && (
          <div
            className="absolute pointer-events-none bg-zinc-900 border border-zinc-700 px-3 py-2 z-10"
            style={{
              left: hoverData.x > width / 2 ? hoverData.x - 170 : hoverData.x + 16,
              top: 16,
            }}
          >
            <p className="text-zinc-400 text-xs mb-1">{hoverData.date}</p>
            <p className="text-white text-sm font-medium tabular-nums">
              {formatPrice(hoverData.price)}
            </p>
            <div className="flex items-center gap-3 mt-1">
              <span
                className={`text-xs tabular-nums ${
                  hoverData.change >= 0 ? 'text-green-500' : 'text-red-500'
                }`}
              >
                {hoverData.change >= 0 ? '+' : ''}
                {hoverData.change.toFixed(2)}%
              </span>
              <span className="text-zinc-400 text-xs tabular-nums">
                {formatVol(hoverData.vol)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}